import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { gcsUploadFile } from '@/lib/gcsStorage';
import { sendNotification } from '@/lib/notifications';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useNavigate, useLocation } from 'react-router-dom';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { PageTransition } from '@/components/PageTransition';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Search,
  Plus,
  MoreVertical,
  Info,
  UserMinus,
  UserPlus,
  ExternalLink,
  Camera,
  Edit2,
  Check,
  CheckCheck,
  Clock,
  Users,
  Hash,
  User,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  PanelLeftClose,
  PanelLeft,
  Trash2,
  Download,
  Image,
  FileText
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import MessageInput from '@/components/MessageInput';
import { getOpenAI } from '@/lib/openai';

// Strip mention format @Name[uuid] to just @Name for display in previews
const stripMentionIds = (text) => {
  if (!text) return text;
  return text.replace(/@([\w\s]+?)\[(?:[a-f0-9-]{36}|ai-assistant)\]/g, '@$1');
};

const ChatPage = () => {
  const { user, userProfile, company } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  usePageTitle('Messages');

  // Get space ID from query parameters
  const queryParams = new URLSearchParams(location.search);
  const spaceIdFromUrl = queryParams.get('space');
  const [conversations, setConversations] = useState([]);

  // Debounced search state
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimerRef = useRef(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isNewChatOpen, setIsNewChatOpen] = useState(false);
  const [newChatType, setNewChatType] = useState('direct'); // 'direct' or 'group'
  const [companyMembers, setCompanyMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const [typing, setTyping] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [conversationUsers, setConversationUsers] = useState({}); // Store user info for conversations
  const messageChannelRef = useRef(null);
  const presenceChannelRef = useRef(null);
  const selectedConversationRef = useRef(null);
  const [deletingConversation, setDeletingConversation] = useState(null);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [lastSeenTimes, setLastSeenTimes] = useState(() => {
    // Load last seen times from localStorage
    const saved = localStorage.getItem('chatLastSeenTimes');
    return saved ? JSON.parse(saved) : {};
  });
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingGroupMembers, setLoadingGroupMembers] = useState(false);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [availableMembers, setAvailableMembers] = useState([]);
  const [selectedNewMembers, setSelectedNewMembers] = useState([]);
  const [uploadingGroupAvatar, setUploadingGroupAvatar] = useState(false);
  const groupAvatarInputRef = useRef(null);
  const [editingGroupName, setEditingGroupName] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [conversationFilter, setConversationFilter] = useState('all'); // 'all' or 'unread'
  const [showSearchModal, setShowSearchModal] = useState(false);

  // Scroll to bottom of messages
  const scrollToBottom = useCallback((instant = false) => {
    messagesEndRef.current?.scrollIntoView({ behavior: instant ? 'instant' : 'smooth' });
  }, []);

  // Debounce search input (300ms)
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load messages when conversation changes
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
    if (selectedConversation?.id) {
      loadMessages(selectedConversation.id, selectedConversation);
      // Load group members for mentions
      loadGroupMembers(selectedConversation.id);
    }
  }, [selectedConversation]);

  // Load conversations
  useEffect(() => {
    if (userProfile?.id && company?.id) {
      loadConversations();
      loadCompanyMembers();
    }
  }, [userProfile?.id, company?.id]);

  // Handle direct message from URL parameter (e.g., from profile page)
  useEffect(() => {
    const userId = queryParams.get('userId');
    if (userId && userProfile?.id && company?.id && !loading && conversations.length > 0) {
      // Check if we already have a conversation with this user
      const existingConv = conversations.find(conv =>
        conv.type === 'direct' && conv.otherUser?.id === userId
      );

      if (existingConv) {
        // If conversation exists, just select it
        setSelectedConversation(existingConv);
        // Clear the userId param from URL
        navigate('/chat', { replace: true });
      } else {
        // Fetch the user profile and start a new chat
        supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .single()
          .then(({ data: otherUser, error }) => {
            if (!error && otherUser) {
              startNewChat(userId, otherUser);
              // Clear the userId param from URL
              navigate('/chat', { replace: true });
            }
          });
      }
    }
  }, [userProfile?.id, company?.id, loading, conversations]);

  // Update conversations online status when onlineUsers changes
  useEffect(() => {
    setConversations(prev => 
      prev.map(conv => {
        if (conv.type === 'direct' && conv.otherUser) {
          return {
            ...conv,
            isOnline: onlineUsers.has(conv.otherUser.id)
          };
        }
        return conv;
      })
    );
    
    // Also update the selected conversation's online status
    if (selectedConversation && selectedConversation.type === 'direct' && selectedConversation.otherUser) {
      setSelectedConversation(prev => ({
        ...prev,
        isOnline: onlineUsers.has(prev.otherUser.id)
      }));
    }
  }, [onlineUsers]);
  
  // Save last seen times to localStorage
  useEffect(() => {
    localStorage.setItem('chatLastSeenTimes', JSON.stringify(lastSeenTimes));
  }, [lastSeenTimes]);

  // Setup realtime subscriptions
  useEffect(() => {
    if (userProfile?.id) {
      setupRealtimeSubscriptions();
      
      // Set up presence heartbeat to keep connection alive
      const heartbeatInterval = setInterval(() => {
        if (presenceChannelRef.current) {
          presenceChannelRef.current.track({
            user_id: userProfile.id,
            online_at: new Date().toISOString(),
            user_info: {
              name: `${userProfile.first_name} ${userProfile.last_name}`,
              email: user?.email
            }
          });
        }
      }, 30000); // Send heartbeat every 30 seconds
      
      // Handle window focus/blur to update presence
      const handleFocus = () => {
        if (presenceChannelRef.current) {
          presenceChannelRef.current.track({
            user_id: userProfile.id,
            online_at: new Date().toISOString(),
            user_info: {
              name: `${userProfile.first_name} ${userProfile.last_name}`,
              email: user?.email
            }
          });
        }
      };
      
      const handleBlur = () => {
        // Window blurred - presence will naturally timeout
      };
      
      window.addEventListener('focus', handleFocus);
      window.addEventListener('blur', handleBlur);
      
      return () => {
        clearInterval(heartbeatInterval);
        window.removeEventListener('focus', handleFocus);
        window.removeEventListener('blur', handleBlur);
        
        if (messageChannelRef.current) {
          supabase.removeChannel(messageChannelRef.current);
          messageChannelRef.current = null;
        }
        if (presenceChannelRef.current) {
          // Untrack presence before removing channel
          presenceChannelRef.current.untrack();
          supabase.removeChannel(presenceChannelRef.current);
          presenceChannelRef.current = null;
        }
      };
    }
    
    return () => {
      if (messageChannelRef.current) {
        supabase.removeChannel(messageChannelRef.current);
        messageChannelRef.current = null;
      }
      if (presenceChannelRef.current) {
        // Untrack presence before removing channel
        presenceChannelRef.current.untrack();
        supabase.removeChannel(presenceChannelRef.current);
        presenceChannelRef.current = null;
      }
    };
  }, [userProfile?.id, userProfile?.first_name, userProfile?.last_name, user?.email]);

  const loadConversations = useCallback(async () => {
    if (!userProfile?.id || !company?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // If entering from a space, ensure the space conversation exists first
      if (spaceIdFromUrl) {
        await supabase.rpc('ensure_space_conversation', {
          p_project_id: spaceIdFromUrl,
          p_user_id: userProfile.id,
          p_company_id: company.id
        });
      }

      // Use optimized RPC that fetches all data in a single query
      const { data, error: fetchError } = await supabase.rpc('get_chat_conversations_data', {
        p_user_id: userProfile.id,
        p_company_id: company.id
      });

      if (fetchError) throw fetchError;

      const conversationsData = data?.conversations || [];

      // Store user info for direct message conversations
      conversationsData.forEach(conv => {
        if (conv.type === 'direct' && conv.otherUser) {
          setConversationUsers(prev => ({
            ...prev,
            [conv.id]: conv.otherUser
          }));
        }
      });

      // Update online status based on current onlineUsers set
      const withOnlineStatus = conversationsData.map(conv => {
        if (conv.type === 'direct' && conv.otherUser?.id) {
          return { ...conv, isOnline: onlineUsers.has(conv.otherUser.id) };
        }
        return conv;
      });

      setConversations(withOnlineStatus);

      // Auto-select space conversation if specified in URL
      if (spaceIdFromUrl && !selectedConversation) {
        const spaceConversation = withOnlineStatus.find(
          conv => conv.type === 'space' && conv.space_id === spaceIdFromUrl
        );
        if (spaceConversation) {
          setSelectedConversation(spaceConversation);
        }
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [userProfile?.id, company?.id, onlineUsers, spaceIdFromUrl, selectedConversation]);

  const loadCompanyMembers = useCallback(async () => {
    if (!company?.id || !userProfile?.id) return;

    try {
      const { data, error } = await supabase
        .from('company_members')
        .select(`
          id,
          role,
          user_profiles (
            id,
            first_name,
            last_name,
            email,
            avatar_url
          )
        `)
        .eq('company_id', company.id)
        .neq('user_id', userProfile.id);

      if (error) throw error;

      // Flatten the data structure for easier use
      const flattenedMembers = (data || []).map(member => ({
        id: member.user_profiles.id,
        first_name: member.user_profiles.first_name,
        last_name: member.user_profiles.last_name,
        email: member.user_profiles.email,
        avatar_url: member.user_profiles.avatar_url,
        role: member.role
      }));

      setCompanyMembers(flattenedMembers);
    } catch (error) {
      console.error('Error loading company members:', error);
    }
  }, [company?.id, userProfile?.id]);

  const loadMessages = useCallback(async (conversationId) => {
    if (!conversationId || !userProfile?.id) {
      setMessages([]);
      return;
    }

    try {
      // Use optimized RPC that fetches messages with sender info and read receipts in one query
      const { data, error: fetchError } = await supabase.rpc('get_conversation_messages_data', {
        p_conversation_id: conversationId,
        p_user_id: userProfile.id,
        p_limit: 100,
        p_offset: 0
      });

      if (fetchError) throw fetchError;

      // Convert timestamps to Date objects
      const formattedMessages = (data?.messages || []).map(msg => ({
        ...msg,
        created_at: msg.created_at ? new Date(msg.created_at) : new Date(),
        edited_at: msg.edited_at ? new Date(msg.edited_at) : null
      }));

      setMessages(formattedMessages);

      // Mark messages as read
      if (formattedMessages.length > 0) {
        await markMessagesAsRead(conversationId);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessages([]);
    }
  }, [userProfile?.id]);

  const markMessagesAsRead = useCallback(async (conversationId) => {
    if (!conversationId || !userProfile?.id) return;

    try {
      await supabase.rpc('mark_messages_as_read', {
        p_conversation_id: conversationId,
        p_user_id: userProfile.id
      });

      // Update local conversation unread count
      setConversations(prev =>
        prev.map(conv =>
          conv.id === conversationId
            ? { ...conv, unreadCount: 0 }
            : conv
        )
      );
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }, [userProfile?.id]);

  const setupRealtimeSubscriptions = () => {
    // Subscribe to new messages in user's conversations
    const messageChannel = supabase
      .channel(`user-messages-${userProfile.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        async (payload) => {
          // Check if this is an AI message
          const isAiMessage = payload.new.sender_id === null;

          // Don't add our own messages (they're already added optimistically)
          // But DO add AI messages (even though we triggered them)
          if (!isAiMessage && payload.new.sender_id === userProfile.id) {
            return;
          }

          let sender = null;

          // Fetch sender info only if not AI message
          if (!isAiMessage) {
            const { data: senderData } = await supabase
              .from('user_profiles')
              .select('id, first_name, last_name, avatar_url')
              .eq('id', payload.new.sender_id)
              .single();
            sender = senderData;
          }

          const newMsg = {
            id: payload.new.id,
            content: payload.new.content,
            sender_id: payload.new.sender_id,
            sender_name: isAiMessage
              ? 'AI Assistant'
              : (sender ? `${sender.first_name} ${sender.last_name}` : 'Unknown'),
            sender_avatar: sender?.avatar_url,
            created_at: new Date(payload.new.created_date),
            delivered: true,
            read: false,
            attachments: payload.new.attachments || [],
            type: payload.new.type || 'text'
          };
          
          // Add to messages if it's the selected conversation
          if (selectedConversationRef.current?.id === payload.new.conversation_id) {
            setMessages(prev => {
              // Check if message already exists
              if (prev.find(m => m.id === newMsg.id)) {
                return prev;
              }
              return [...prev, newMsg];
            });
            // Mark as read if user is viewing this conversation
            markMessagesAsRead(payload.new.conversation_id);
          }
          
          // Update conversation list
          setConversations(prev => {
            const updated = prev.map(conv =>
              conv.id === payload.new.conversation_id
                ? {
                    ...conv,
                    unreadCount: selectedConversationRef.current?.id === payload.new.conversation_id
                      ? 0
                      : (conv.unreadCount || 0) + 1,
                    lastMessage: payload.new.content.substring(0, 100),
                    lastMessageTime: new Date(payload.new.created_date)
                  }
                : conv
            );

            // Re-sort conversations by most recent
            return updated.sort((a, b) => {
              const dateA = new Date(a.lastMessageTime || 0);
              const dateB = new Date(b.lastMessageTime || 0);
              return dateB - dateA;
            });
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'message_reads'
        },
        (payload) => {
          // Update the read status of the message
          setMessages(prev => 
            prev.map(msg => 
              msg.id === payload.new.message_id 
                ? { ...msg, read: true }
                : msg
            )
          );
        }
      )
      .subscribe(() => {
        // Message channel subscribed
      });
    
    messageChannelRef.current = messageChannel;
    
    // Subscribe to presence for online status
    const presenceChannel = supabase.channel('online-users', {
      config: {
        presence: {
          key: userProfile.id, // Use user ID as the presence key
        },
      },
    })
      .on('presence', { event: 'sync' }, () => {
        const state = presenceChannel.presenceState();

        // Extract all online user IDs from the presence state
        const onlineUserIds = new Set();
        Object.keys(state).forEach(key => {
          const presences = state[key];
          if (Array.isArray(presences)) {
            presences.forEach(presence => {
              if (presence.user_id) {
                onlineUserIds.add(presence.user_id);
              }
            });
          }
        });
        
        setOnlineUsers(onlineUserIds);
      })
      .on('presence', { event: 'join' }, ({ newPresences }) => {
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          newPresences.forEach(presence => {
            if (presence.user_id) {
              newSet.add(presence.user_id);
            }
          });
          return newSet;
        });
      })
      .on('presence', { event: 'leave' }, ({ leftPresences }) => {
        
        // Track last seen time for users who left
        const now = new Date();
        leftPresences.forEach(presence => {
          if (presence.user_id) {
            setLastSeenTimes(prev => ({
              ...prev,
              [presence.user_id]: now
            }));
          }
        });
        
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          leftPresences.forEach(presence => {
            if (presence.user_id) {
              newSet.delete(presence.user_id);
            }
          });
          return newSet;
        });
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await presenceChannel.track({
            user_id: userProfile.id,
            online_at: new Date().toISOString(),
            user_info: {
              name: `${userProfile.first_name} ${userProfile.last_name}`,
              email: user?.email
            }
          });
        }
      });
    
    presenceChannelRef.current = presenceChannel;
  };

  const loadGroupMembers = async (conversationId) => {
    setLoadingGroupMembers(true);
    try {
      const { data, error } = await supabase
        .from('conversation_members')
        .select(`
          user:user_profiles (
            id,
            first_name,
            last_name,
            avatar_url,
            email,
            job_title
          ),
          role,
          joined_date
        `)
        .eq('conversation_id', conversationId);
      
      if (error) throw error;
      
      const members = data.map(item => ({
        ...item.user,
        role: item.role,
        joined_date: item.joined_date
      }));
      
      setGroupMembers(members);
    } catch (error) {
      console.error('Error loading group members:', error);
    } finally {
      setLoadingGroupMembers(false);
    }
  };

  const loadAvailableMembers = async () => {
    if (!company?.id || !selectedConversation?.id) return;
    
    try {
      // Get current group members
      const { data: currentMembers } = await supabase
        .from('conversation_members')
        .select('user_id')
        .eq('conversation_id', selectedConversation.id);
      
      const currentMemberIds = currentMembers?.map(m => m.user_id) || [];
      
      // Get all company members not in the group
      const { data: companyMembersData } = await supabase
        .from('company_members')
        .select(`
          user_profiles (
            id,
            first_name,
            last_name,
            avatar_url,
            email
          )
        `)
        .eq('company_id', company.id)
        .not('user_id', 'in', `(${currentMemberIds.join(',')})`);
      
      setAvailableMembers(companyMembersData || []);
    } catch (error) {
      console.error('Error loading available members:', error);
    }
  };

  const removeMemberFromGroup = async (memberId) => {
    if (!selectedConversation?.id) return;
    
    if (confirm('Are you sure you want to remove this member from the group?')) {
      try {
        // Get the member's name before removing
        const memberToRemove = groupMembers.find(m => m.id === memberId);
        const memberName = memberToRemove ? `${memberToRemove.first_name} ${memberToRemove.last_name}` : 'Member';
        
        const { error } = await supabase
          .from('conversation_members')
          .delete()
          .eq('conversation_id', selectedConversation.id)
          .eq('user_id', memberId);
        
        if (error) throw error;
        
        // Add system message about member removal
        const systemMessage = {
          conversation_id: selectedConversation.id,
          sender_id: userProfile.id,
          content: `${userProfile.first_name} ${userProfile.last_name} removed ${memberName} from the group`,
          type: 'system',
          created_date: new Date().toISOString()
        };
        
        const { data: newMessage, error: msgError } = await supabase
          .from('messages')
          .insert(systemMessage)
          .select()
          .single();
        
        if (!msgError && newMessage) {
          // Add the system message to the chat
          setMessages(prev => [...prev, {
            ...newMessage,
            sender_name: 'System',
            created_at: new Date(newMessage.created_date)
          }]);
        }
        
        // Reload group members
        loadGroupMembers(selectedConversation.id);
        
        // Update member count
        setSelectedConversation(prev => ({
          ...prev,
          memberCount: (prev.memberCount || 0) - 1
        }));
      } catch (error) {
        console.error('Error removing member:', error);
        alert('Failed to remove member from group');
      }
    }
  };

  const addMemberToChat = async (memberId) => {
    if (!selectedConversation || !memberId) return;

    try {
      // Add member to conversation
      const { error } = await supabase
        .from('conversation_members')
        .insert({
          conversation_id: selectedConversation.id,
          user_id: memberId
        });

      if (error) throw error;

      // Reload group members to update the list
      await loadGroupMembers(selectedConversation.id);

      // Update member count in selected conversation
      setSelectedConversation(prev => ({
        ...prev,
        memberCount: (prev.memberCount || 0) + 1
      }));

      // Add system message about member being added
      const memberData = companyMembers.find(m => m.id === memberId);
      if (memberData) {
        const systemMessageContent = `${userProfile.first_name} ${userProfile.last_name} added ${memberData.first_name} ${memberData.last_name} to the chat`;

        const systemMessage = {
          conversation_id: selectedConversation.id,
          sender_id: userProfile.id,
          content: systemMessageContent,
          type: 'system',
          created_date: new Date().toISOString()
        };

        const { data: insertedMessage } = await supabase
          .from('messages')
          .insert(systemMessage)
          .select()
          .single();

        // Add to local messages state immediately
        if (insertedMessage) {
          const formattedMessage = {
            id: insertedMessage.id,
            content: insertedMessage.content,
            type: 'system',
            sender_id: insertedMessage.sender_id,
            sender_name: 'System',
            created_at: new Date(insertedMessage.created_date),
            delivered: true,
            read: false,
            attachments: []
          };

          setMessages(prev => [...prev, formattedMessage]);

          // Scroll to bottom to show the new message
          setTimeout(() => scrollToBottom(), 100);
        }
      }
    } catch (error) {
      console.error('Error adding member to chat:', error);
      throw error;
    }
  };

  const addMembersToGroup = async () => {
    if (!selectedConversation?.id || selectedNewMembers.length === 0) return;

    try {
      // Get member names for the system message
      const memberNames = availableMembers
        .filter(m => selectedNewMembers.includes(m.user_profiles.id))
        .map(m => `${m.user_profiles.first_name} ${m.user_profiles.last_name}`);
      
      const membersToAdd = selectedNewMembers.map(userId => ({
        conversation_id: selectedConversation.id,
        user_id: userId,
        role: 'member',
        joined_date: new Date().toISOString()
      }));
      
      const { error } = await supabase
        .from('conversation_members')
        .insert(membersToAdd);
      
      if (error) throw error;
      
      // Add system message about members being added
      const systemMessage = {
        conversation_id: selectedConversation.id,
        sender_id: userProfile.id,
        content: `${userProfile.first_name} ${userProfile.last_name} added ${memberNames.join(', ')} to the group`,
        type: 'system',
        created_date: new Date().toISOString()
      };
      
      const { data: newMessage, error: msgError } = await supabase
        .from('messages')
        .insert(systemMessage)
        .select()
        .single();
      
      if (!msgError && newMessage) {
        // Add the system message to the chat
        setMessages(prev => [...prev, {
          ...newMessage,
          sender_name: 'System',
          created_at: new Date(newMessage.created_date)
        }]);
      }
      
      // Reload group members
      loadGroupMembers(selectedConversation.id);
      
      // Update member count
      setSelectedConversation(prev => ({
        ...prev,
        memberCount: (prev.memberCount || 0) + selectedNewMembers.length
      }));
      
      // Reset and close
      setSelectedNewMembers([]);
      setShowAddMemberDialog(false);
    } catch (error) {
      console.error('Error adding members:', error);
      alert('Failed to add members to group');
    }
  };

  const removeSelectedFile = useCallback((index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const uploadGroupAvatar = async (file) => {
    if (!selectedConversation?.id) return;
    
    setUploadingGroupAvatar(true);
    try {
      const fileExt = file.name.split('.').pop();

      // Upload to GCS
      const { publicUrl } = await gcsUploadFile(file, {
        folderPath: `chat-attachments/group-avatars/${selectedConversation.id}`,
        fileName: `${Date.now()}.${fileExt}`,
        contentType: file.type
      });
      
      // Update conversation avatar in database
      const { error: updateError } = await supabase
        .from('conversations')
        .update({ avatar: publicUrl })
        .eq('id', selectedConversation.id);
      
      if (updateError) throw updateError;
      
      // Update local state
      setSelectedConversation(prev => ({
        ...prev,
        avatar: publicUrl
      }));
      
      // Update in conversations list
      setConversations(prev => 
        prev.map(conv => 
          conv.id === selectedConversation.id 
            ? { ...conv, avatar: publicUrl }
            : conv
        )
      );
      
      // Add system message about avatar change
      const systemMessage = {
        conversation_id: selectedConversation.id,
        sender_id: userProfile.id,
        content: `${userProfile.first_name} ${userProfile.last_name} updated the group photo`,
        type: 'system',
        created_date: new Date().toISOString()
      };
      
      const { data: newMessage } = await supabase
        .from('messages')
        .insert(systemMessage)
        .select()
        .single();
      
      if (newMessage) {
        setMessages(prev => [...prev, {
          ...newMessage,
          sender_name: 'System',
          created_at: new Date(newMessage.created_date)
        }]);
      }
    } catch (error) {
      console.error('Error uploading group avatar:', error);
      alert('Failed to upload group avatar');
    } finally {
      setUploadingGroupAvatar(false);
    }
  };

  const updateGroupName = async () => {
    if (!selectedConversation?.id || !newGroupName.trim()) return;
    
    try {
      // Update group name in database
      const { error } = await supabase
        .from('conversations')
        .update({ name: newGroupName.trim() })
        .eq('id', selectedConversation.id);
      
      if (error) throw error;
      
      // Update local state
      setSelectedConversation(prev => ({
        ...prev,
        name: newGroupName.trim()
      }));
      
      // Update in conversations list
      setConversations(prev => 
        prev.map(conv => 
          conv.id === selectedConversation.id 
            ? { ...conv, name: newGroupName.trim() }
            : conv
        )
      );
      
      // Add system message about name change
      const systemMessage = {
        conversation_id: selectedConversation.id,
        sender_id: userProfile.id,
        content: `${userProfile.first_name} ${userProfile.last_name} renamed the group to "${newGroupName.trim()}"`,
        type: 'system',
        created_date: new Date().toISOString()
      };
      
      const { data: newMessage } = await supabase
        .from('messages')
        .insert(systemMessage)
        .select()
        .single();
      
      if (newMessage) {
        setMessages(prev => [...prev, {
          ...newMessage,
          sender_name: 'System',
          created_at: new Date(newMessage.created_date)
        }]);
      }
      
      setEditingGroupName(false);
      setNewGroupName('');
    } catch (error) {
      console.error('Error updating group name:', error);
      alert('Failed to update group name');
    }
  };

  const handleGroupAvatarSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 5MB for avatars)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image is too large. Maximum size is 5MB.');
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file.');
        return;
      }
      
      uploadGroupAvatar(file);
    }
    e.target.value = ''; // Reset input
  };

  const handleFilesSelected = useCallback((files) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        alert(`File ${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  }, []);

  const uploadFiles = useCallback(async () => {
    const uploadedAttachments = [];
    
    for (const file of selectedFiles) {
      try {
        const fileExt = file.name.split('.').pop();

        const { publicUrl } = await gcsUploadFile(file, {
          folderPath: `chat-attachments/${userProfile.id}`,
          fileName: `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`,
          contentType: file.type
        });
        
        uploadedAttachments.push({
          type: file.type.startsWith('image/') ? 'image' : 'file',
          url: publicUrl,
          name: file.name,
          size: file.size,
          mimeType: file.type
        });
      } catch (err) {
        console.error('Unexpected error uploading file:', err);
        alert(`Unexpected error uploading ${file.name}`);
      }
    }

    return uploadedAttachments;
  }, [selectedFiles, userProfile?.id]);

  const sendMessage = useCallback(async (messageContent) => {
    if ((!messageContent && selectedFiles.length === 0) || !selectedConversation) {
      return;
    }

    setSending(true);
    setUploadingFiles(true);
    
    try {
      // Upload files first if any
      let attachments = [];
      if (selectedFiles.length > 0) {
        attachments = await uploadFiles();
      }
      
      // Optimistically add message to UI
      const tempId = `temp-${Date.now()}`;
      const tempMessage = {
        id: tempId,
        content: messageContent || (attachments.length > 0 ? '📎 Attachment' : ''),
        sender_id: userProfile.id,
        sender_name: `${userProfile.first_name} ${userProfile.last_name}`,
        sender_avatar: userProfile.avatar_url,
        created_at: new Date(),
        delivered: false,
        read: false,
        attachments: attachments
      };

      setMessages(prev => [...prev, tempMessage]);
      setSelectedFiles([]);
      
      // Scroll to bottom when sending
      setTimeout(() => scrollToBottom(), 100);

      // Determine message type based on attachments
      let messageType = 'text';
      if (attachments.length > 0) {
        // If all attachments are images, use 'image', otherwise use 'file'
        messageType = attachments.every(a => a.type === 'image') ? 'image' : 'file';
      }
      
      // Extract mentioned user IDs from the message using the unique format @Name[id]
      // Matches both UUIDs (36 chars) and special IDs like 'ai-assistant'
      const mentionedUserIds = [];
      const mentionRegex = /@[\w\s]+\[([a-f0-9-]{36}|ai-assistant)\]/g;
      let match;

      while ((match = mentionRegex.exec(messageContent)) !== null) {
        const userId = match[1];
        mentionedUserIds.push(userId);
      }

      // Filter out ai-assistant from mentions for database (mentions column is uuid[] type)
      const dbMentions = mentionedUserIds.filter(id => id !== 'ai-assistant');

      // Send to Supabase
      const { data, error } = await supabase
        .from('messages')
        .insert({
          conversation_id: selectedConversation.id,
          content: messageContent || '',
          sender_id: userProfile.id,
          type: messageType,
          attachments: attachments,
          mentions: dbMentions.length > 0 ? dbMentions : null
        })
        .select(`
          id,
          content,
          created_date,
          sender:user_profiles!sender_id (
            id,
            first_name,
            last_name,
            avatar_url
          )
        `)
        .single();

      if (error) throw error;

      // Create notifications for mentioned users
      if (mentionedUserIds.length > 0) {
        const mentionedUserIdsFiltered = mentionedUserIds
          .filter(userId => userId !== userProfile.id && userId !== 'ai-assistant'); // Don't notify yourself or AI

        if (mentionedUserIdsFiltered.length > 0) {
          await sendNotification({
            notification_type: 'mentions',
            user_ids: mentionedUserIdsFiltered,
            title: 'You were mentioned',
            message: `${userProfile.first_name} ${userProfile.last_name} mentioned you in ${selectedConversation.name || 'a chat'}`,
            link_url: `/chat?conversation=${selectedConversation.id}`,
            entity_type: 'chat_message',
            entity_id: selectedConversation.id,
            actor_id: userProfile.id,
            group_key: `chat_${selectedConversation.id}_mentions`
          });
        }
      }

      // Check if AI Assistant was mentioned
      const aiMentioned = mentionedUserIds.includes('ai-assistant');
      if (aiMentioned) {
        // Extract the question/message for the AI (remove the @AI Assistant mention)
        const questionText = messageContent.replace(/@AI Assistant\[ai-assistant\]/g, '').trim();

        // Generate AI response in the background with streaming
        setTimeout(async () => {
          try {
            const openai = getOpenAI();
            if (!openai) {
              console.error('OpenAI service not initialized');
              return;
            }

            // Create a temporary AI message that will be updated as we stream
            const tempAiId = `ai-temp-${Date.now()}`;
            const tempAiMessage = {
              id: tempAiId,
              content: '',
              type: 'text',
              sender_id: null,
              sender_name: 'AI Assistant',
              sender_avatar: null,
              created_at: new Date(),
              delivered: false,
              read: false,
              attachments: [],
              isStreaming: true
            };

            setMessages(prev => [...prev, tempAiMessage]);
            setTimeout(() => scrollToBottom(), 100);

            // Stream the AI response
            let fullResponse = '';
            const response = await openai.teamChatAssistantStream([
              { role: 'user', content: questionText }
            ], attachments, (chunk) => {
              // Update message content as we receive chunks
              fullResponse += chunk;
              setMessages(prev =>
                prev.map(msg =>
                  msg.id === tempAiId
                    ? { ...msg, content: fullResponse }
                    : msg
                )
              );
              setTimeout(() => scrollToBottom(), 10);
            });

            if (response.success) {
              // Post the complete AI response to database
              const { data: aiMessage, error: aiError } = await supabase
                .from('messages')
                .insert({
                  conversation_id: selectedConversation.id,
                  content: fullResponse,
                  sender_id: null,
                  type: 'text',
                  created_date: new Date().toISOString()
                })
                .select()
                .single();

              if (aiError) {
                console.error('Error posting AI response:', aiError);
                return;
              }

              // Replace temp message with real one
              if (aiMessage) {
                setMessages(prev =>
                  prev.map(msg =>
                    msg.id === tempAiId
                      ? {
                          id: aiMessage.id,
                          content: aiMessage.content,
                          type: 'text',
                          sender_id: null,
                          sender_name: 'AI Assistant',
                          sender_avatar: null,
                          created_at: new Date(aiMessage.created_date),
                          delivered: true,
                          read: false,
                          attachments: [],
                          isStreaming: false
                        }
                      : msg
                  )
                );
              }
            }
          } catch (error) {
            console.error('Error generating AI response:', error);
          }
        }, 1000);
      }

      // Don't mark own message as read - we only care about recipient's read status

      // Replace temp message with real one
      setMessages(prev =>
        prev.map(msg =>
          msg.id === tempId
            ? {
                id: data.id,
                content: data.content,
                sender_id: data.sender.id,
                sender_name: `${data.sender.first_name} ${data.sender.last_name}`,
                sender_avatar: data.sender.avatar_url,
                created_at: new Date(data.created_date),
                delivered: true,
                read: false,
                attachments: attachments
              }
            : msg
        )
      );

      // Update conversation list with new message preview and timestamp
      setConversations(prev => {
        const updated = prev.map(conv =>
          conv.id === selectedConversation.id
            ? {
                ...conv,
                lastMessage: messageContent || '📎 Attachment',
                lastMessageTime: new Date(data.created_date)
              }
            : conv
        );

        // Re-sort conversations by most recent
        return updated.sort((a, b) => {
          const dateA = new Date(a.lastMessageTime || 0);
          const dateB = new Date(b.lastMessageTime || 0);
          return dateB - dateA;
        });
      });

    } catch (error) {
      console.error('Error sending message:', error);
      alert(`Failed to send message: ${error.message}`);
      // Remove temp message on error
      setMessages(prev => prev.filter(msg => !msg.id.startsWith('temp-')));
      // Note: MessageInput component manages its own state, so message will be preserved on error
    } finally {
      setSending(false);
      setUploadingFiles(false);
    }
  }, [selectedConversation, userProfile, selectedFiles, uploadFiles]);


  const deleteConversation = async (conversationId, e) => {
    // Prevent clicking through to select the conversation
    if (e) {
      e.stopPropagation();
    }
    
    try {
      setDeletingConversation(conversationId);
      
      // Delete the conversation (messages will be cascade deleted)
      const { error } = await supabase
        .from('conversations')
        .delete()
        .eq('id', conversationId);

      if (error) throw error;

      // Remove from local state
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      
      // If this was the selected conversation, clear selection
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }
      
      setDeletingConversation(null);
    } catch (error) {
      console.error('Error deleting conversation:', error);
      setDeletingConversation(null);
      alert('Failed to delete conversation. You may not have permission.');
    }
  };

  const createGroupChat = async () => {
    if (!groupName.trim() || selectedMembers.length === 0) {
      alert('Please enter a group name and select at least one member');
      return;
    }
    
    try {
      // Call the RPC function to create group chat
      const { data: conversationId, error } = await supabase.rpc('create_group_chat', {
        p_name: groupName.trim(),
        p_creator_id: userProfile.id,
        p_member_ids: selectedMembers,
        p_company_id: company.id
      });
      
      if (error) throw error;
      
      // Create conversation object for UI
      const newConversation = {
        id: conversationId,
        type: 'group',
        name: groupName.trim(),
        avatar: null,
        lastMessage: 'Group chat created',
        lastMessageTime: new Date(),
        unreadCount: 0,
        memberCount: selectedMembers.length + 1, // +1 for creator
        isOnline: false
      };
      
      // Add to conversations
      setConversations(prev => [newConversation, ...prev]);
      
      // Select the new conversation
      setSelectedConversation(newConversation);
      
      // Close dialog and reset
      setIsNewChatOpen(false);
      setSelectedMembers([]);
      setGroupName('');
      setNewChatType('direct');
      
    } catch (error) {
      console.error('Error creating group chat:', error);
      alert('Failed to create group chat: ' + error.message);
    }
  };

  const startNewChat = async (otherUserId, otherUser) => {
    try {
      // Find or create direct message conversation
      const { data: conversationId, error } = await supabase.rpc('find_or_create_direct_message', {
        p_user1_id: userProfile.id,
        p_user2_id: otherUserId,
        p_company_id: company.id
      });

      if (error) throw error;

      // Create conversation object for UI
      const newConversation = {
        id: conversationId,
        type: 'direct',
        name: `${otherUser.first_name} ${otherUser.last_name}`,
        avatar: otherUser.avatar_url,
        otherUser: otherUser,
        lastMessage: 'Start a conversation',
        lastMessageTime: new Date(),
        unreadCount: 0,
        isOnline: onlineUsers.has(otherUserId)
      };

      // Add to conversations if not already there
      setConversations(prev => {
        const exists = prev.find(c => c.id === conversationId);
        if (exists) {
          return prev;
        }
        return [newConversation, ...prev];
      });

      // Select the conversation (loadMessages will be triggered by useEffect)
      setSelectedConversation(newConversation);
      setIsNewChatOpen(false);
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const filteredConversations = useMemo(() => {
    return conversations.filter(conv => {
      // When viewing from a space, only show the space conversation + direct messages
      if (spaceIdFromUrl) {
        const isSpaceConv = conv.type === 'space' && conv.space_id === spaceIdFromUrl;
        const isDirect = conv.type === 'direct';
        if (!isSpaceConv && !isDirect) return false;
      }

      // Apply search filter (using debounced search)
      const matchesSearch = conv.name.toLowerCase().includes(debouncedSearch.toLowerCase());
      if (!matchesSearch) return false;

      // Apply conversation filter
      if (conversationFilter === 'unread') {
        return conv.unreadCount > 0;
      }

      // 'all' filter shows everything
      return true;
    });
  }, [conversations, debouncedSearch, conversationFilter, spaceIdFromUrl]);

  const ConversationItem = ({ conversation, isSelected }) => {
    const isOnline = conversation.type === 'direct' && conversation.isOnline;
    
    // Helper function to truncate text
    const truncateText = (text, maxLength = 45) => {
      const cleaned = stripMentionIds(text);
      if (cleaned.length <= maxLength) return cleaned;
      return cleaned.substring(0, maxLength) + '...';
    };
    
    // Collapsed view - just show avatar with badge
    if (isSidebarCollapsed) {
      return (
        <div
          className={`flex items-center justify-center p-2 cursor-pointer transition-colors relative ${
            isSelected 
              ? 'bg-primary/10 border-l-4 border-primary' 
              : 'hover:bg-muted border-l-4 border-transparent'
          }`}
          onClick={() => {
            setSelectedConversation(conversation);
          }}
          title={conversation.name}
        >
          <div className="relative">
            <Avatar className="h-10 w-10">
              <AvatarImage src={conversation.avatar} />
              <AvatarFallback>
                {conversation.type === 'channel' ? (
                  <Hash className="h-5 w-5" />
                ) : conversation.type === 'group' ? (
                  <Users className="h-5 w-5" />
                ) : conversation.type === 'space' ? (
                  <Hash className="h-5 w-5" />
                ) : (
                  conversation.name.charAt(0).toUpperCase()
                )}
              </AvatarFallback>
            </Avatar>
            {isOnline && (
              <div className="absolute bottom-0 right-0 h-3 w-3 bg-green-500 border-2 border-background rounded-full" />
            )}
            {conversation.unreadCount > 0 && (
              <div className="absolute -top-1 -right-1 h-5 w-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center text-xs font-bold">
                {conversation.unreadCount > 9 ? '9+' : conversation.unreadCount}
              </div>
            )}
          </div>
        </div>
      );
    }
    
    // Expanded view - full conversation item
    return (
      <div
        className={`block px-4 py-3 cursor-pointer transition-colors ${
          isSelected 
            ? 'bg-primary/10 border-l-4 border-primary' 
            : 'hover:bg-muted border-l-4 border-transparent'
        }`}
        onClick={() => {
          setSelectedConversation(conversation);
        }}
      >
        <div className="flex items-start gap-3">
          {/* Avatar - Fixed width */}
          <div className="w-10 h-10 relative flex-shrink-0">
            <Avatar className="h-10 w-10">
              <AvatarImage src={conversation.avatar} />
              <AvatarFallback>
                {conversation.type === 'channel' ? (
                  <Hash className="h-5 w-5" />
                ) : conversation.type === 'group' ? (
                  <Users className="h-5 w-5" />
                ) : conversation.type === 'space' ? (
                  <Hash className="h-5 w-5" />
                ) : (
                  conversation.name.charAt(0).toUpperCase()
                )}
              </AvatarFallback>
            </Avatar>
            {isOnline && (
              <div className="absolute bottom-0 right-0 h-3 w-3 bg-green-500 border-2 border-background rounded-full" />
            )}
          </div>
          
          {/* Content - Calculate remaining width */}
          <div className="w-[calc(100%-52px)]">
            {/* Name and Time Row */}
            <div className="flex items-center justify-between gap-2 mb-1">
              <h4 className="font-medium text-sm" title={conversation.name}>
                {truncateText(conversation.name, 30)}
              </h4>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {formatDistanceToNow(conversation.lastMessageTime, { addSuffix: true })
                  .replace('about ', '')}
              </span>
            </div>
            
            {/* Message and Badge Row */}
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground" title={stripMentionIds(conversation.lastMessage)}>
                {truncateText(conversation.lastMessage, 45)}
              </p>
              {conversation.unreadCount > 0 && (
                <Badge variant="default" className="h-5 min-w-[20px] px-1.5 flex-shrink-0">
                  {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const MessageBubble = ({ message, isOwn, showTimestamp = true, showAvatar = true, showName = true }) => {
    // Check if this is a system message
    const isSystemMessage = message.type === 'system';
    
    // Render system messages differently
    if (isSystemMessage) {
      return (
        <div className="flex justify-center my-2">
          <div className="bg-muted/50 rounded-full px-3 py-1 text-xs text-muted-foreground">
            {message.content}
          </div>
        </div>
      );
    }
    
    return (
      <div className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}>
        {!isOwn && (
          showAvatar ? (
            <Avatar className="h-8 w-8">
              <AvatarImage src={message.sender_avatar} />
              <AvatarFallback>
                {message.sender_name ? message.sender_name.charAt(0).toUpperCase() : 'U'}
              </AvatarFallback>
            </Avatar>
          ) : (
            <div className="w-8" /> // Spacer to align messages
          )
        )}
        
        <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
          {!isOwn && showName && message.sender_name && (
            <span className="text-xs text-muted-foreground mb-1">{message.sender_name}</span>
          )}
          <div
            className={`rounded-lg px-3 py-2 ${
              isOwn
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted'
            }`}
          >
            {message.content && (
              <div className="text-sm">
                {(() => {
                  // Parse message content to highlight mentions (with format @Name[id])
                  const content = message.content;
                  const mentions = [];

                  // Regex to match @Name[uuid] or @Name[ai-assistant] format
                  const mentionRegex = /@([\w\s]+)\[([a-f0-9-]{36}|ai-assistant)\]/g;
                  let match;

                  while ((match = mentionRegex.exec(content)) !== null) {
                    mentions.push({
                      start: match.index,
                      end: match.index + match[0].length,
                      text: `@${match[1]}`, // Display without the [id] part
                      userId: match[2]
                    });
                  }

                  // If no mentions, return plain content
                  if (mentions.length === 0) {
                    return content;
                  }

                  // Sort mentions by position
                  mentions.sort((a, b) => a.start - b.start);

                  // Build parts array with text and mentions
                  const parts = [];
                  let lastIndex = 0;

                  mentions.forEach((mention) => {
                    // Skip overlapping mentions
                    if (mention.start < lastIndex) return;

                    // Add text before mention
                    if (mention.start > lastIndex) {
                      parts.push({
                        type: 'text',
                        content: content.substring(lastIndex, mention.start)
                      });
                    }

                    // Add mention
                    parts.push({
                      type: 'mention',
                      content: mention.text,
                      userId: mention.userId
                    });

                    lastIndex = mention.end;
                  });

                  // Add remaining text after last mention
                  if (lastIndex < content.length) {
                    parts.push({
                      type: 'text',
                      content: content.substring(lastIndex)
                    });
                  }

                  // Render parsed parts
                  return parts.map((part, index) => {
                    if (part.type === 'mention') {
                      // Check if it's AI Assistant mention (no profile to navigate to)
                      const isAiMention = part.userId === 'ai-assistant';

                      return (
                        <button
                          key={`mention-${index}`}
                          onClick={() => !isAiMention && navigate(`/profile/${part.userId}`)}
                          className={`font-bold transition-all text-blue-600 hover:text-blue-800 ${
                            isAiMention ? 'cursor-default' : 'cursor-pointer'
                          }`}
                        >
                          {part.content}
                        </button>
                      );
                    }
                    return <span key={`text-${index}`}>{part.content}</span>;
                  });
                })()}
              </div>
            )}
            
            {/* Display attachments */}
            {message.attachments && message.attachments.length > 0 && (
              <div className="mt-2 space-y-2">
                {message.attachments.map((attachment, index) => (
                  <div key={index}>
                    {attachment.type === 'image' ? (
                      <img 
                        src={attachment.url} 
                        alt={attachment.name}
                        className="max-w-sm rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => {
                          setSelectedImage(attachment);
                          setImageModalOpen(true);
                        }}
                      />
                    ) : (
                      <a
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 bg-background/10 rounded-lg hover:bg-background/20 transition-colors"
                      >
                        <FileText className="h-4 w-4 flex-shrink-0" />
                        <span className="text-sm truncate flex-1">{attachment.name}</span>
                        <Download className="h-4 w-4 flex-shrink-0" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {showTimestamp && (
            <div className="flex items-center gap-1 mt-1">
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(message.created_at || message.created_date, { addSuffix: true })}
              </span>
              {isOwn && (
                <span className="text-xs text-muted-foreground">
                  {message.read ? (
                    <CheckCheck className="h-3 w-3 inline" />
                  ) : message.delivered ? (
                    <Check className="h-3 w-3 inline" />
                  ) : (
                    <Clock className="h-3 w-3 inline" />
                  )}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };


  return (
    <AppShell3>
      <PageTransition>
      <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
        {/* Sidebar */}
        <div
          className={`border-r bg-background flex flex-col flex-shrink-0 transition-all duration-300 h-full overflow-hidden ${
            isSidebarCollapsed ? 'w-20' : 'w-96'
          }`}
        >
          {/* Sidebar Header */}
          <div className={`${isSidebarCollapsed ? 'p-2' : 'p-4'} border-b`}>
            {isSidebarCollapsed ? (
              /* Collapsed state - show collapse button centered */
              <div className="flex justify-center">
                <Button 
                  size="icon" 
                  variant="ghost"
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  title="Expand sidebar"
                >
                  <PanelLeft className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              /* Expanded state - show full header */
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Messages</h2>
                <div className="flex items-center gap-1">
                  <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="icon" variant="ghost">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            setNewChatType('direct');
                            setSelectedMembers([]);
                            setGroupName('');
                            setIsNewChatOpen(true);
                          }}
                        >
                          <User className="h-4 w-4 mr-2" />
                          Direct Message
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setNewChatType('group');
                            setSelectedMembers([]);
                            setGroupName('');
                            setIsNewChatOpen(true);
                          }}
                        >
                          <Users className="h-4 w-4 mr-2" />
                          Group Chat
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  <Button 
                    size="icon" 
                    variant="ghost"
                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                    title="Collapse sidebar"
                  >
                    <PanelLeftClose className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            
            {/* Search - only show when expanded */}
            {!isSidebarCollapsed && (
              <div className="relative mt-4">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search conversations..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => {
                    // Optional: Open search modal for better UX on small screens
                    if (window.innerWidth < 768) {
                      setShowSearchModal(true);
                    }
                  }}
                />
              </div>
            )}
          </div>

          {/* Conversation Tabs - only show when expanded */}
          {!isSidebarCollapsed && (
            <div className="flex border-b px-4 gap-1">
              <Button 
                variant="ghost" 
                size="sm" 
                className="flex-1 rounded-none border-b-2 border-transparent data-[active=true]:border-primary data-[active=true]:bg-transparent" 
                data-active={conversationFilter === 'all'}
                onClick={() => setConversationFilter('all')}
              >
                All
                {conversations.length > 0 && (
                  <span className="ml-1 text-xs text-muted-foreground">({conversations.length})</span>
                )}
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="flex-1 rounded-none border-b-2 border-transparent data-[active=true]:border-primary data-[active=true]:bg-transparent"
                data-active={conversationFilter === 'unread'}
                onClick={() => setConversationFilter('unread')}
              >
                Unread
                {conversations.filter(c => c.unreadCount > 0).length > 0 && (
                  <Badge variant="secondary" className="ml-1 px-1 py-0 h-4 text-xs">
                    {conversations.filter(c => c.unreadCount > 0).length}
                  </Badge>
                )}
              </Button>
            </div>
          )}

          {/* Quick Actions for Collapsed State */}
          {isSidebarCollapsed && (
            <div className="flex flex-col items-center gap-2 p-2 border-b">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="icon" variant="ghost" title="New Chat">
                    <Plus className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem
                    onClick={() => {
                      setNewChatType('direct');
                      setSelectedMembers([]);
                      setIsNewChatOpen(true);
                    }}
                  >
                    <User className="h-4 w-4 mr-2" />
                    Direct Message
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setNewChatType('group');
                      setSelectedMembers([]);
                      setIsNewChatOpen(true);
                    }}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Group Chat
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button 
                size="icon" 
                variant="ghost" 
                title="Search"
                onClick={() => setShowSearchModal(true)}
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            <div className="py-1 pb-4">
              {/* Spaces Section */}
              {filteredConversations.filter(c => c.type === 'space').length > 0 && (
                <>
                  {!isSidebarCollapsed ? (
                    <div className="px-4 py-2 sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <Hash className="h-3 w-3" />
                        Spaces
                      </h3>
                    </div>
                  ) : (
                    <div className="py-2 flex justify-center">
                      <Hash className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  {filteredConversations
                    .filter(c => c.type === 'space')
                    .map(conversation => (
                      <ConversationItem
                        key={conversation.id}
                        conversation={conversation}
                        isSelected={selectedConversation?.id === conversation.id}
                      />
                    ))}
                </>
              )}

              {/* Add separator between Spaces and Channels */}
              {filteredConversations.filter(c => c.type === 'space').length > 0 &&
               filteredConversations.filter(c => c.type === 'channel').length > 0 && (
                <Separator className="my-2" />
              )}

              {/* Channels Section */}
              {filteredConversations.filter(c => c.type === 'channel').length > 0 && (
                <>
                  {!isSidebarCollapsed ? (
                    <div className="px-4 py-2 sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <Hash className="h-3 w-3" />
                        Channels
                      </h3>
                    </div>
                  ) : (
                    <div className="py-2 flex justify-center">
                      <Hash className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  {filteredConversations
                    .filter(c => c.type === 'channel')
                    .map(conversation => (
                      <ConversationItem
                        key={conversation.id}
                        conversation={conversation}
                        isSelected={selectedConversation?.id === conversation.id}
                      />
                    ))}
                </>
              )}

              {/* Add separator between Channels and Groups */}
              {filteredConversations.filter(c => c.type === 'channel').length > 0 &&
               filteredConversations.filter(c => c.type === 'group').length > 0 && (
                <Separator className="my-2" />
              )}

              {/* Add separator between Spaces and Groups if no Channels */}
              {filteredConversations.filter(c => c.type === 'space').length > 0 &&
               filteredConversations.filter(c => c.type === 'channel').length === 0 &&
               filteredConversations.filter(c => c.type === 'group').length > 0 && (
                <Separator className="my-2" />
              )}

              {/* Groups Section */}
              {filteredConversations.filter(c => c.type === 'group').length > 0 && (
                <>
                  {!isSidebarCollapsed ? (
                    <div className="px-4 py-2 sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <Users className="h-3 w-3" />
                        Groups
                      </h3>
                    </div>
                  ) : (
                    <div className="py-2 flex justify-center">
                      <Users className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  {filteredConversations
                    .filter(c => c.type === 'group')
                    .map(conversation => (
                      <ConversationItem
                        key={conversation.id}
                        conversation={conversation}
                        isSelected={selectedConversation?.id === conversation.id}
                      />
                    ))}
                </>
              )}
              
              {/* Add separator between Groups and Direct Messages */}
              {filteredConversations.filter(c => c.type === 'group').length > 0 && 
               filteredConversations.filter(c => c.type === 'direct').length > 0 && (
                <Separator className="my-2" />
              )}
              
              {/* Direct Messages Section */}
              {filteredConversations.filter(c => c.type === 'direct').length > 0 && (
                <>
                  {!isSidebarCollapsed ? (
                    <div className="px-4 py-2 sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <MessageCircle className="h-3 w-3" />
                        Direct Messages
                      </h3>
                    </div>
                  ) : (
                    <div className="py-2 flex justify-center">
                      <MessageCircle className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  {filteredConversations
                    .filter(c => c.type === 'direct')
                    .map(conversation => (
                      <ConversationItem
                        key={conversation.id}
                        conversation={conversation}
                        isSelected={selectedConversation?.id === conversation.id}
                      />
                    ))}
                </>
              )}
              
              {/* Empty state */}
              {filteredConversations.length === 0 && (
                <div className="px-4 py-8 text-center">
                  <p className="text-sm text-muted-foreground">No conversations found</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
          {selectedConversation ? (
            <div className="flex flex-col h-full">
              {/* Chat Header */}
              <div className="h-16 border-b px-6 flex items-center justify-between bg-background flex-shrink-0">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={selectedConversation.avatar} />
                    <AvatarFallback>
                      {selectedConversation.type === 'channel' ? (
                        <Hash className="h-5 w-5" />
                      ) : selectedConversation.type === 'group' ? (
                        <Users className="h-5 w-5" />
                      ) : (
                        selectedConversation.name.charAt(0).toUpperCase()
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold">{selectedConversation.name}</h3>
                    <p className="text-xs text-muted-foreground">
                      {selectedConversation.type === 'direct' 
                        ? selectedConversation.isOnline 
                          ? 'Active now' 
                          : lastSeenTimes[selectedConversation.otherUser?.id]
                            ? `Last seen ${formatDistanceToNow(lastSeenTimes[selectedConversation.otherUser.id], { addSuffix: true })}`
                            : 'Offline'
                        : `${selectedConversation.memberCount} members`
                      }
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Info Dialog - show for direct messages, groups, and spaces */}
                  {(selectedConversation.type === 'direct' || selectedConversation.type === 'group' || selectedConversation.type === 'space') && (
                    <Dialog
                      open={showProfileDialog}
                      onOpenChange={(open) => {
                        setShowProfileDialog(open);
                        if (open && (selectedConversation.type === 'group' || selectedConversation.type === 'space')) {
                          loadGroupMembers(selectedConversation.id);
                        }
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button size="icon" variant="ghost">
                          <Info className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                          <DialogTitle>
                            {selectedConversation.type === 'direct' ? 'User Profile' :
                             selectedConversation.type === 'space' ? 'Space Details' : 'Group Details'}
                          </DialogTitle>
                        </DialogHeader>
                        {selectedConversation.type === 'direct' ? (
                          /* Direct Message Profile */
                          <div className="flex flex-col items-center space-y-4 py-4">
                            {/* User Avatar */}
                            <Avatar className="h-24 w-24">
                              <AvatarImage src={selectedConversation.otherUser?.avatar_url} />
                              <AvatarFallback className="text-2xl">
                                {selectedConversation.otherUser?.first_name?.charAt(0).toUpperCase() || 'U'}
                              </AvatarFallback>
                            </Avatar>
                            
                            {/* User Name */}
                            <div className="text-center">
                              <h3 className="text-lg font-semibold">
                                {selectedConversation.otherUser?.first_name} {selectedConversation.otherUser?.last_name}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                {selectedConversation.otherUser?.email}
                              </p>
                            </div>
                            
                            {/* User Details */}
                            <div className="w-full space-y-2">
                              {selectedConversation.otherUser?.job_title && (
                                <div className="flex justify-between">
                                  <span className="text-sm text-muted-foreground">Job Title:</span>
                                  <span className="text-sm font-medium">{selectedConversation.otherUser.job_title}</span>
                                </div>
                              )}
                              {selectedConversation.otherUser?.department && (
                                <div className="flex justify-between">
                                  <span className="text-sm text-muted-foreground">Department:</span>
                                  <span className="text-sm font-medium">{selectedConversation.otherUser.department}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Status:</span>
                                <span className="text-sm font-medium">
                                  {selectedConversation.isOnline ? (
                                    <span className="text-green-600">Online</span>
                                  ) : lastSeenTimes[selectedConversation.otherUser?.id] ? (
                                    <span className="text-muted-foreground">
                                      Last seen {formatDistanceToNow(lastSeenTimes[selectedConversation.otherUser.id], { addSuffix: true })}
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground">Offline</span>
                                  )}
                                </span>
                              </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex gap-2 w-full">
                              <Button 
                                className="flex-1"
                                onClick={() => {
                                  setShowProfileDialog(false);
                                  // Navigate to the user's specific profile page
                                  navigate(`/profile/${selectedConversation.otherUser.id}`);
                                }}
                              >
                                <User className="h-4 w-4 mr-2" />
                                View Full Profile
                              </Button>
                            </div>
                          </div>
                        ) : (
                          /* Group/Space Chat Members */
                          <div className="py-4">
                            {/* Group/Space Info */}
                            <div className="flex flex-col items-center mb-4">
                              {/* Group/Space Avatar with Upload */}
                              <div className="relative group">
                                <Avatar className="h-20 w-20 mb-3">
                                  <AvatarImage src={selectedConversation.avatar} />
                                  <AvatarFallback>
                                    {selectedConversation.type === 'space' ? (
                                      <Hash className="h-10 w-10" />
                                    ) : (
                                      <Users className="h-10 w-10" />
                                    )}
                                  </AvatarFallback>
                                </Avatar>
                                {groupMembers.find(m => m.id === userProfile?.id)?.role === 'admin' && selectedConversation.type !== 'space' && (
                                  <>
                                    <input
                                      ref={groupAvatarInputRef}
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={handleGroupAvatarSelect}
                                    />
                                    <Button
                                      size="icon"
                                      variant="secondary"
                                      className="absolute bottom-2 right-0 h-8 w-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                      onClick={() => groupAvatarInputRef.current?.click()}
                                      disabled={uploadingGroupAvatar}
                                    >
                                      <Camera className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                              
                              {/* Group Name with Edit */}
                              <div className="flex items-center gap-2">
                                {editingGroupName ? (
                                  <div className="flex items-center gap-2">
                                    <Input
                                      value={newGroupName}
                                      onChange={(e) => setNewGroupName(e.target.value)}
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                          updateGroupName();
                                        }
                                      }}
                                      className="h-8 w-48"
                                      autoFocus
                                    />
                                    <Button
                                      size="sm"
                                      onClick={updateGroupName}
                                      disabled={!newGroupName.trim()}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setEditingGroupName(false);
                                        setNewGroupName('');
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <h3 className="text-lg font-semibold">{selectedConversation.name}</h3>
                                    {groupMembers.find(m => m.id === userProfile?.id)?.role === 'admin' && selectedConversation.type !== 'space' && (
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-6 w-6"
                                        onClick={() => {
                                          setNewGroupName(selectedConversation.name);
                                          setEditingGroupName(true);
                                        }}
                                      >
                                        <Edit2 className="h-3 w-3" />
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {selectedConversation.memberCount} members
                              </p>
                              {selectedConversation.type === 'space' && selectedConversation.space_id && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="mt-2"
                                  onClick={() => navigate(`/space/${selectedConversation.space_id}`)}
                                >
                                  <ExternalLink className="h-3 w-3 mr-2" />
                                  View Space
                                </Button>
                              )}
                            </div>

                            <Separator className="my-4" />
                            
                            {/* Members List */}
                            <div className="space-y-1">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-medium">Members</h4>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setShowAddMemberDialog(true);
                                    loadAvailableMembers();
                                  }}
                                >
                                  <UserPlus className="h-4 w-4 mr-1" />
                                  Add Members
                                </Button>
                              </div>
                              <ScrollArea className="h-[300px]">
                                <div className="space-y-2">
                                  {loadingGroupMembers ? (
                                    <div className="text-center py-4">
                                      <LoadingSpinner />
                                    </div>
                                  ) : groupMembers.length > 0 ? (
                                    groupMembers.map((member) => {
                                      const isCurrentUser = member.id === userProfile?.id;
                                      const currentUserIsAdmin = groupMembers.find(m => m.id === userProfile?.id)?.role === 'admin';
                                      
                                      return (
                                        <div key={member.id} className="flex items-center gap-3 p-2 hover:bg-muted rounded-lg group">
                                          <Avatar className="h-8 w-8">
                                            <AvatarImage src={member.avatar_url} />
                                            <AvatarFallback>
                                              {member.first_name?.charAt(0).toUpperCase() || 'U'}
                                            </AvatarFallback>
                                          </Avatar>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                              <p className="text-sm font-medium truncate">
                                                {member.first_name} {member.last_name}
                                                {isCurrentUser && ' (You)'}
                                              </p>
                                              {member.role === 'admin' && (
                                                <Badge variant="secondary" className="text-xs">Admin</Badge>
                                              )}
                                              {onlineUsers.has(member.id) && (
                                                <div className="h-2 w-2 bg-green-500 rounded-full" />
                                              )}
                                            </div>
                                            <p className="text-xs text-muted-foreground truncate">
                                              {member.email}
                                            </p>
                                          </div>
                                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            {!isCurrentUser && (
                                              <>
                                                <Button
                                                  size="icon"
                                                  variant="ghost"
                                                  className="h-8 w-8"
                                                  onClick={() => {
                                                    setShowProfileDialog(false);
                                                    navigate(`/profile/${member.id}`);
                                                  }}
                                                  title="View Profile"
                                                >
                                                  <ExternalLink className="h-4 w-4" />
                                                </Button>
                                                {currentUserIsAdmin && member.role !== 'admin' && (
                                                  <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                                    onClick={() => removeMemberFromGroup(member.id)}
                                                    title="Remove from group"
                                                  >
                                                    <UserMinus className="h-4 w-4" />
                                                  </Button>
                                                )}
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <p className="text-sm text-muted-foreground text-center py-4">
                                      No members found
                                    </p>
                                  )}
                                </div>
                              </ScrollArea>
                            </div>
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                  )}
                  
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="icon" variant="ghost">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => deleteConversation(selectedConversation.id)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Conversation
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-6 messages-container min-h-0">
                <div className="pb-4">
                  {messages.filter(msg => msg && msg.id).map((message, index, filteredMessages) => {
                    const prevMessage = index > 0 ? filteredMessages[index - 1] : null;
                    const nextMessage = index < filteredMessages.length - 1 ? filteredMessages[index + 1] : null;
                    
                    // Don't group system messages
                    const isSystemMessage = message.type === 'system';
                    
                    // Check if this message should be grouped with the previous one
                    const isSameSenderAsPrev = !isSystemMessage && prevMessage && prevMessage.type !== 'system' && prevMessage.sender_id === message.sender_id;
                    const isSameSenderAsNext = !isSystemMessage && nextMessage && nextMessage.type !== 'system' && nextMessage.sender_id === message.sender_id;
                    
                    // Calculate time difference with previous message (in seconds)
                    const timeDiffWithPrev = prevMessage 
                      ? (new Date(message.created_date || message.created_at) - new Date(prevMessage.created_date || prevMessage.created_at)) / 1000
                      : Infinity;
                    
                    // Calculate time difference with next message (in seconds)
                    const timeDiffWithNext = nextMessage 
                      ? (new Date(nextMessage.created_date || nextMessage.created_at) - new Date(message.created_date || message.created_at)) / 1000
                      : Infinity;
                    
                    // Group messages if they're from the same sender and within 60 seconds (but not system messages)
                    const shouldGroupWithPrev = !isSystemMessage && isSameSenderAsPrev && timeDiffWithPrev < 60;
                    const shouldGroupWithNext = !isSystemMessage && isSameSenderAsNext && timeDiffWithNext < 60;
                    
                    // Show avatar and name only for the first message in a group
                    const showAvatar = !shouldGroupWithPrev;
                    const showName = !shouldGroupWithPrev;
                    
                    // Show timestamp only for the last message in a group
                    const showTimestamp = !shouldGroupWithNext;
                    
                    // Add spacing based on grouping
                    const marginClass = shouldGroupWithPrev ? 'mt-1' : 'mt-4';
                    
                    return (
                      <div key={message.id} className={index === 0 ? '' : marginClass}>
                        <MessageBubble
                          message={message}
                          isOwn={message.sender_id === userProfile?.id}
                          showTimestamp={showTimestamp}
                          showAvatar={showAvatar}
                          showName={showName}
                        />
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Message Input */}
              <MessageInput
                onSendMessage={sendMessage}
                onFilesSelected={handleFilesSelected}
                selectedFiles={selectedFiles}
                onRemoveFile={removeSelectedFile}
                uploadingFiles={uploadingFiles}
                sending={sending}
                conversationMembers={selectedConversation?.type !== 'direct' ? groupMembers : []}
                allCompanyMembers={selectedConversation?.type !== 'direct' ? companyMembers : []}
                onAddMemberToConversation={selectedConversation?.type !== 'direct' ? addMemberToChat : null}
              />
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Select a conversation</h3>
                <p className="text-muted-foreground mb-4">
                  Choose a conversation from the list or start a new one
                </p>
                <Button onClick={() => setIsNewChatOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Message
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      </PageTransition>
      
      {/* Image Preview Modal */}
      <Dialog open={imageModalOpen} onOpenChange={setImageModalOpen}>
        <DialogContent className="max-w-4xl w-full p-0">
          <div className="relative">
            {selectedImage && (
              <>
                <div className="p-4 border-b">
                  <DialogHeader>
                    <DialogTitle>{selectedImage.name}</DialogTitle>
                    <DialogDescription>
                      {(selectedImage.size / 1024).toFixed(1)} KB
                    </DialogDescription>
                  </DialogHeader>
                </div>
                <div className="p-4 max-h-[70vh] overflow-auto flex items-center justify-center bg-muted/50">
                  <img 
                    src={selectedImage.url} 
                    alt={selectedImage.name}
                    className="max-w-full max-h-full object-contain rounded-lg"
                  />
                </div>
                <div className="p-4 border-t flex justify-between items-center">
                  <Button
                    variant="outline"
                    onClick={() => setImageModalOpen(false)}
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = selectedImage.url;
                      link.download = selectedImage.name;
                      link.click();
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Add Members to Group Dialog */}
      <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Members to Group</DialogTitle>
            <DialogDescription>
              Select team members to add to this group chat
            </DialogDescription>
          </DialogHeader>
          
          <Command>
            <CommandInput placeholder="Search team members..." />
            <CommandList>
              <CommandEmpty>No members found.</CommandEmpty>
              <CommandGroup heading="Available Members">
                {availableMembers.map((member) => (
                  <CommandItem
                    key={member.user_profiles.id}
                    onSelect={() => {
                      setSelectedNewMembers(prev => {
                        const isSelected = prev.includes(member.user_profiles.id);
                        if (isSelected) {
                          return prev.filter(id => id !== member.user_profiles.id);
                        } else {
                          return [...prev, member.user_profiles.id];
                        }
                      });
                    }}
                  >
                    <div className={`h-4 w-4 mr-2 rounded border flex items-center justify-center ${
                      selectedNewMembers.includes(member.user_profiles.id)
                        ? 'bg-primary border-primary' 
                        : 'border-muted-foreground'
                    }`}>
                      {selectedNewMembers.includes(member.user_profiles.id) && (
                        <Check className="h-3 w-3 text-primary-foreground" />
                      )}
                    </div>
                    <Avatar className="h-8 w-8 mr-2">
                      <AvatarImage src={member.user_profiles?.avatar_url} />
                      <AvatarFallback>
                        {member.user_profiles?.first_name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">
                        {member.user_profiles?.first_name} {member.user_profiles?.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {member.user_profiles?.email}
                      </p>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          
          {selectedNewMembers.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground mb-2">
                Selected: {selectedNewMembers.length} member{selectedNewMembers.length > 1 ? 's' : ''}
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setSelectedNewMembers([]);
                    setShowAddMemberDialog(false);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={addMembersToGroup}
                  className="flex-1"
                  disabled={selectedNewMembers.length === 0}
                >
                  Add to Group
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Search Modal for Collapsed Sidebar */}
      <Dialog open={showSearchModal} onOpenChange={setShowSearchModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Search Conversations</DialogTitle>
            <DialogDescription>
              Search for conversations, groups, or people
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search conversations..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
            
            {/* Search Results */}
            <ScrollArea className="h-[300px]">
              <div className="space-y-1">
                {filteredConversations.length > 0 ? (
                  filteredConversations.map(conversation => (
                    <div
                      key={conversation.id}
                      className="flex items-center gap-3 p-2 hover:bg-muted rounded-lg cursor-pointer"
                      onClick={() => {
                        setSelectedConversation(conversation);
                        setShowSearchModal(false);
                        setSearchQuery('');
                      }}
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={conversation.avatar} />
                        <AvatarFallback>
                          {conversation.type === 'group' ? (
                            <Users className="h-4 w-4" />
                          ) : (
                            conversation.name?.charAt(0).toUpperCase() || 'U'
                          )}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {conversation.name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {conversation.type === 'group'
                            ? `${conversation.memberCount || 0} members`
                            : stripMentionIds(conversation.lastMessage)
                          }
                        </p>
                      </div>
                      {conversation.unreadCount > 0 && (
                        <Badge variant="default" className="ml-auto">
                          {conversation.unreadCount}
                        </Badge>
                      )}
                    </div>
                  ))
                ) : searchQuery ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No conversations found matching "{searchQuery}"
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Start typing to search conversations
                  </p>
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* New Chat Dialog - Available globally */}
      <Dialog open={isNewChatOpen} onOpenChange={(open) => {
        setIsNewChatOpen(open);
        if (!open) {
          // Reset when closing
          setSelectedMembers([]);
          setGroupName('');
          setNewChatType('direct');
        }
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {newChatType === 'direct' ? 'New Direct Message' : 'New Group Chat'}
            </DialogTitle>
            <DialogDescription>
              {newChatType === 'direct' 
                ? 'Select a team member to start a conversation'
                : 'Select team members to create a group chat'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Chat Type Toggle */}
          <div className="flex gap-2 mb-4">
            <Button
              variant={newChatType === 'direct' ? 'default' : 'outline'}
              className="flex-1"
              onClick={() => {
                setNewChatType('direct');
                setSelectedMembers([]);
              }}
            >
              <User className="h-4 w-4 mr-2" />
              Direct Message
            </Button>
            <Button
              variant={newChatType === 'group' ? 'default' : 'outline'}
              className="flex-1"
              onClick={() => {
                setNewChatType('group');
                setSelectedMembers([]);
              }}
            >
              <Users className="h-4 w-4 mr-2" />
              Group Chat
            </Button>
          </div>
          
          {/* Group Name Input (for group chats only) */}
          {newChatType === 'group' && (
            <div className="mb-4">
              <label className="text-sm font-medium mb-2 block">Group Name</label>
              <Input
                placeholder="Enter group name..."
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className="w-full"
              />
            </div>
          )}
          
          <Command>
            <CommandInput placeholder="Search team members..." />
            <CommandList>
              <CommandEmpty>No members found.</CommandEmpty>
              <CommandGroup heading="Team Members">
                {companyMembers.map((member) => (
                  <CommandItem
                    key={member.id}
                    onSelect={() => {
                      if (newChatType === 'direct') {
                        startNewChat(member.id, member);
                      } else {
                        // For group chat, toggle selection
                        setSelectedMembers(prev => {
                          const isSelected = prev.includes(member.id);
                          if (isSelected) {
                            return prev.filter(id => id !== member.id);
                          } else {
                            return [...prev, member.id];
                          }
                        });
                      }
                    }}
                  >
                    {newChatType === 'group' && (
                      <div className={`h-4 w-4 mr-2 rounded border flex items-center justify-center ${
                        selectedMembers.includes(member.id)
                          ? 'bg-primary border-primary'
                          : 'border-muted-foreground'
                      }`}>
                        {selectedMembers.includes(member.id) && (
                          <Check className="h-3 w-3 text-primary-foreground" />
                        )}
                      </div>
                    )}
                    <Avatar className="h-8 w-8 mr-2">
                      <AvatarImage src={member.avatar_url} />
                      <AvatarFallback>
                        {member.first_name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">
                        {member.first_name} {member.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {member.email}
                      </p>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          {newChatType === 'group' && selectedMembers.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground mb-2">
                Selected: {selectedMembers.length} member{selectedMembers.length > 1 ? 's' : ''}
              </p>
              <Button 
                onClick={createGroupChat}
                className="w-full"
                disabled={!groupName.trim() || selectedMembers.length === 0}
              >
                Create Group Chat
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell3>
  );
};

export default ChatPage;